import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { compact, intersection } from "lodash";
import { getFormByType } from "../paths";
import { APPROVAL_STATUS, formTypeIdentifiers, formTypeIdentifiersProfessional, formTypeIdentifiersUnderGrad } from "../../util/constants";

export default function useFormSubmission(props = {}) {
    const navigate = useNavigate();
    const user = useSelector((state) => (state?.authentication?.user ?? {}));
    const { applicationRoles, gradPlanAdminPlans, isEnrollmentAdmin, isFacultyApprover, isGradPlanAdmin, isGraduationAdmin, isProfessionalStudent, isUnderGradStudent } = user;
    const osuid = (props?.osuid ?? user.osuid);

    const isUserCreatorOfForm = useCallback((form) => {
        return (form.createdBy === osuid);
    }, [osuid]);

    const isUserStudentOnForm = useCallback((form) => {
        return (form.studentid === osuid);
    }, [osuid]);

    const canUserAccessFormType = useCallback((formType) => {
        // professional students only have access to specific forms
        if (applicationRoles.length === 1 && isProfessionalStudent === true) {
            return (formTypeIdentifiersProfessional.includes(formType));
        }
        // undergraduate students only have access to specific forms
        if (applicationRoles.length === 1 && isUnderGradStudent === true) {
            return (formTypeIdentifiersUnderGrad.includes(formType));
        }
        return true;
    }, [applicationRoles, isProfessionalStudent, isUnderGradStudent]);

    const canUserActOnForm = useCallback((form = {}) => {
        const formStatus = form?.status;
        const formType = form?.formType;
        const isGradFacultyNominationForm = (formType === formTypeIdentifiers.GRAD_FACULTY_NOMINATION);
        if (formType) {
            if (formStatus === APPROVAL_STATUS.DRAFT) {
                return (compact([form?.requesterId, form.studentid]).includes(osuid));
            }
            if (formStatus === APPROVAL_STATUS.PENDING) {
                const { getAdminRoles } = getFormByType(formType);
                const adminRoles = (getAdminRoles ? getAdminRoles(form) : []);
                const isAdmin = (intersection(applicationRoles, adminRoles).length > 0);
                if (isGradFacultyNominationForm === true) {
                    return (isAdmin === true || isFacultyApprover === true);
                } else {
                    return isAdmin;
                }
            }
            if (formStatus === APPROVAL_STATUS.APPROVED) {
                const { getAdminRoles } = getFormByType(formType);
                const adminRoles = (getAdminRoles ? getAdminRoles(form) : []);
                const isAdmin = (intersection(applicationRoles, adminRoles).length > 0);
                return isAdmin;
            }
        }
        return false;
    }, [applicationRoles, isFacultyApprover, osuid]);

    const canUserCancelForm = useCallback((form = {}) => {
        return (canUserActOnForm(form) ||
            (form?.formType === formTypeIdentifiers.APPLICATION_TO_GRADUATE &&
                form?.status === APPROVAL_STATUS.APPROVED &&
                isUserStudentOnForm(form))
        );
    }, [canUserActOnForm, isUserStudentOnForm]);

    const navigateToFormLandingPage = useCallback((formTypeFriendly, errorMessage = null) => {
        const options = {};
        if (errorMessage) options.state = { errorMessage };
        navigate(`/form/${formTypeFriendly}`, options);
    }, [navigate]);

    const validateEditPermitted = useCallback((form) => {
        let error = null;
        if ((isEnrollmentAdmin || isGraduationAdmin)) {
            if ([APPROVAL_STATUS.CANCELLED, APPROVAL_STATUS.COMPLETED].includes(form.status)) {
                error = "This form cannot be edited once it has been completed or cancelled.";
            }
        } else {
            if ((!isUserStudentOnForm(form) && !isUserCreatorOfForm(form)) || form.status !== APPROVAL_STATUS.DRAFT) {
                error = "This form cannot be edited once it has been submitted.";
            }
        }
        return error;
    }, [isUserCreatorOfForm, isUserStudentOnForm, isEnrollmentAdmin, isGraduationAdmin]);

    return {
        canUserAccessFormType,
        canUserActOnForm,
        canUserCancelForm,
        navigateToFormLandingPage,
        validateEditPermitted
    };
}